/**
 * =============================================================================
 * Hook: useAffiliateDashboardKpis
 * Versão: 3.1.0 (Wrapper do overview com suporte a período)
 *
 * Mudanças v3.1.0:
 *   - Repassa `{ startDate, endDate }` para `useAffiliateHomeOverview`,
 *     permitindo que os KPIs derivados (clicks/referrals/conversions/conversion)
 *     reflitam o período selecionado pelo afiliado.
 *   - Saldos (`availableBalance`, `totalEarned`, etc.) continuam acumulados.
 * =============================================================================
 */
import {
  useAffiliateHomeOverview,
  type UseAffiliateHomeOverviewParams,
} from '@/hooks/useAffiliateHomeOverview';

export interface AffiliateDashboardKpis {
  totalClicks: number;
  totalReferrals: number;
  totalConversions: number;
  conversionRate: number;
  totalEarned: number;
  availableBalance: number;
  pendingPayout: number;
  totalPaid: number;
  /**
   * Valor de payouts em status `pending` ou `processing` —
   * dinheiro que o afiliado já solicitou sacar e está aguardando
   * processamento financeiro. Vem direto do ledger via
   * `_calc_affiliate_balance.in_transit`.
   */
  inTransit: number;
}

export function useAffiliateDashboardKpis(
  params: UseAffiliateHomeOverviewParams = {},
) {
  const q = useAffiliateHomeOverview(params);
  const o = q.data;

  const kpis: AffiliateDashboardKpis | undefined = o
    ? {
        totalClicks: o.funnel_lifetime.clicks,
        totalReferrals: o.funnel_lifetime.referrals,
        totalConversions: o.funnel_lifetime.conversions,
        conversionRate: o.funnel_lifetime.conversion_rate,
        totalEarned: Number(o.balance.total_earned ?? 0),
        availableBalance: Number(o.balance.available ?? 0),
        // "pendingPayout" antigo agora reflete o que o ledger considera "available"
        // (aprovado e ainda não atrelado a payout). Mantido por compatibilidade.
        pendingPayout: Number(o.balance.available ?? 0),
        totalPaid: Number(o.balance.total_paid ?? 0),
        inTransit: Number(o.balance.in_transit ?? 0),
      }
    : undefined;

  return {
    ...q,
    data: kpis,
  };
}
